import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import config from '../../../config';
import { NdoeLoading, NdoeError, NdoeEmpty, NdoeStatusBadge } from '../../../components/ndoe';
import { messageErreur } from '../../../components/ndoe/chargement';

/*
  INFORMATION DU PRODUIT — le panneau du tiroir de l'écran Produits.

  ⚠ IL S'AFFICHAIT SANS AUCUN STYLE (signalé le 22/08/2026). Il portait les
  classes `mouvClient-row` sans importer de feuille : ces classes vivent dans
  les SCSS des pages Mouvements / Ventes / Livraisons. Depuis Produits, rien ne
  les chargeait — texte géant, « : » à nu, alignement cassé. Le style est
  désormais dans le design system (ndoe-fiche) : le motif sert à deux écrans,
  il ne peut pas appartenir à une page.

  ⚠ ET IL NE DISAIT RIEN QUAND ÇA ALLAIT MAL. `useState([])` + un fetch sans
  `catch` : une API en panne rendait exactement le même écran qu'un produit
  sans données — des tirets. On distingue maintenant les trois cas (chargement,
  panne, vide), comme partout ailleurs dans l'application.
*/
const ProductDetail = ({ idProduit }) => {
  const DOMAIN = config.REACT_APP_SERVER_DOMAIN;
  const [data, setData] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);

  const charger = useCallback(async () => {
    if (!idProduit) return;
    setChargement(true);
    setErreur(null);
    try {
      const { data: res } = await axios.get(`${DOMAIN}/api/produit/produit/${idProduit}`);
      setData(Array.isArray(res) ? res[0] || null : res || null);
    } catch (e) {
      setErreur(messageErreur(e, 'la fiche du produit'));
    } finally {
      setChargement(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idProduit]);

  useEffect(() => { charger(); }, [charger]);

  // Une date absente ou invalide ne doit pas rendre « Invalid date » :
  // moment le faisait, date-fns lèverait. On tranche avant de formater.
  const jour = (v) => {
    if (!v) return null;
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : format(d, 'dd-MM-yyyy');
  };

  const Ligne = ({ libelle, valeur, num = false }) => (
    <div className="ndoe-fiche__ligne">
      <span className="ndoe-fiche__libelle">{libelle}</span>
      {valeur === null || valeur === undefined || valeur === ''
        ? <span className="ndoe-fiche__valeur ndoe-fiche__vide">non renseigné</span>
        : <span className={`ndoe-fiche__valeur${num ? ' num' : ''}`}>{valeur}</span>}
    </div>
  );

  if (chargement) return <NdoeLoading rows={6} />;
  if (erreur) return <NdoeError title="La fiche n’a pas pu être chargée" text={erreur} onRetry={charger} />;
  if (!data) return <NdoeEmpty icon="👟" title="Fiche introuvable" text="Ce modèle n’existe plus ou a été supprimé." />;

  return (
    <div className="ndoe-fiche">
      <Ligne libelle="Nom du modèle" valeur={data.nom_produit} />
      <Ligne libelle="Référence" valeur={data.code_produit} num />
      <Ligne libelle="Marque" valeur={data.nom_marque} />
      <Ligne libelle="Catégorie" valeur={data.nom_categorie} />
      <Ligne libelle="Matière" valeur={data.nom_matiere} />
      <Ligne libelle="Famille" valeur={data.nom_famille} />
      <Ligne libelle="Cible" valeur={data.nom_cible} />
      <Ligne
        libelle="Prix"
        num
        valeur={data.prix === null || data.prix === undefined || data.prix === ''
          ? null
          : `${Number(data.prix).toLocaleString('fr-CD')} $`}
      />
      <Ligne libelle="Code variante" valeur={data.code_variante} num />
      <Ligne libelle="Entré le" valeur={jour(data.date_entrant)} num />
      <Ligne libelle="Mis à jour le" valeur={jour(data.date_MisAjour)} num />
      {/* L'état est le seul champ qui porte un JUGEMENT (actif / inactif) :
          il prend un badge, pas du texte bleu en dur comme auparavant. */}
      <div className="ndoe-fiche__ligne">
        <span className="ndoe-fiche__libelle">État</span>
        <span>
          {data.etatProduit
            ? <NdoeStatusBadge kind="custom" tone={data.etatProduit === 'Actif' ? 'ok' : 'neutral'} label={data.etatProduit} />
            : <span className="ndoe-fiche__valeur ndoe-fiche__vide">non renseigné</span>}
        </span>
      </div>
    </div>
  );
};

export default ProductDetail;
